//#region IMPORTS
import { InstoreTemplateRow } from "../../models/instoreTemplate";
import {
  CodeBracketSquareIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { formatPrice } from "../../helpers/formatPrice";
import formatWeekOrderArticleName from "../../helpers/formatWeekOrderArticleName";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { useAppDispatch } from "../../store/configureStore";
import { closeModal, openModal } from "../../features/dashBoard/dashboardSlice";
import { WEEK_ORDER_IMAGE_PREVIEW_MODAL } from "../../models/_consts";
import { woodWingImagesEnabled } from "../../api/woodwingImages";
import useWoodWingImages from "../../hooks/useWoodWingImages";
import WeekOrderItemThumbnail from "./WeekOrderItemThumbnail";
import WeekOrderImagePreview from "./WeekOrderImagePreview";
import MainModal from "../_layout/MainModal";
//#endregion

// Rows grow from 48px to 152px: a 128px square for the WoodWing thumbnail
// with 12px above and below. The thumbnail file is 256px on its long side, so
// 128px is as large as it stays sharp on a retina screen. Without the image
// API configured the list stays as it was.
// ⚠️ The virtualizer caches row sizes and doesn't re-read estimateSize, so in
// dev a hot reload after changing this keeps the old height (rows overlap)
// until the theme is reopened or the page reloaded.
const ROW_HEIGHT = woodWingImagesEnabled ? 152 : 48;

//#region INTERFACE
interface Props {
  items: InstoreTemplateRow[];
  quantityByRowId: Map<number, number>;
  onRowAdd: (row: InstoreTemplateRow, quantity: number) => void;
  disabled?: boolean;
  showStoreFormat?: boolean;
}
//#endregion

export default function WeekOrderThemeItems({
  items,
  quantityByRowId,
  onRowAdd,
  disabled = false,
  showStoreFormat = false,
}: Props) {
  //#region SETUP
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [qtyOverrides, setQtyOverrides] = useState<Record<number, number>>({});
  // Kept after closing, so the modal doesn't empty out during its fade-out.
  const [previewRow, setPreviewRow] = useState<InstoreTemplateRow | null>(null);

  const virtualizer = useVirtualizer({
    getScrollElement: () => containerRef.current,
    estimateSize: () => ROW_HEIGHT,
    count: items.length,
    overscan: 6,
  });
  const virtualRows = virtualizer.getVirtualItems();

  // The rendered rows (visible + overscan) are the "visible set": one image
  // request for whatever of it isn't cached yet. The article name is the
  // image's file name in WoodWing.
  const getImage = useWoodWingImages(
    virtualRows.map((virtualRow) => items[virtualRow.index]?.article_name),
  );

  const getQty = (row: InstoreTemplateRow) =>
    qtyOverrides[row.id] ?? row.amount;
  //#endregion

  //#region LOGIC
  const openPreview = (row: InstoreTemplateRow) => {
    setPreviewRow(row);
    dispatch(openModal(WEEK_ORDER_IMAGE_PREVIEW_MODAL));
  };
  const closePreview = () => dispatch(closeModal());
  //#endregion

  //#region RENDER
  return (
    <div
      ref={containerRef}
      className="border-t border-gray-100 max-h-[60vh] overflow-auto"
    >
      <ul
        className="relative"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualRows.map((virtualRow) => {
          const row = items[virtualRow.index];
          const inBasket = quantityByRowId.get(row.id) ?? 0;
          const hasPerso = (row.custom_fields?.length ?? 0) > 0;
          const qty = getQty(row);
          const addDisabled = disabled || !qty || qty < 1;
          const image = getImage(row.article_name);
          return (
            <li
              key={row.id}
              className="absolute left-0 right-0 flex items-center justify-between gap-4 px-6 border-b border-gray-100"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {woodWingImagesEnabled && (
                <WeekOrderItemThumbnail
                  src={image?.thumbnail}
                  alt={formatWeekOrderArticleName(row.article_name)}
                  onOpen={() => openPreview(row)}
                />
              )}
              <div className="min-w-0 flex-1 flex flex-col">
                <div className="flex items-center gap-3 min-w-0">
                  <p className="truncate text-base font-semibold text-gray-900">
                    {formatWeekOrderArticleName(row.article_name) || "—"}
                  </p>
                  {inBasket > 0 && (
                    <span className="shrink-0 inline-flex items-center justify-center rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold h-6 min-w-6 px-2">
                      {inBasket}
                    </span>
                  )}
                  {hasPerso && (
                    <span
                      data-tooltip-id={"weekPerso" + row.id}
                      data-tooltip-content={t("rituals.persoInfoBasketTooltip")}
                      className="shrink-0 text-purple-600"
                    >
                      <CodeBracketSquareIcon className="h-5 w-5" />
                      <Tooltip id={"weekPerso" + row.id} />
                    </span>
                  )}
                </div>
                {showStoreFormat && row.store_format && (
                  <p className="truncate text-sm text-gray-500">
                    {row.store_format}
                  </p>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-4">
                {/* Reference values from the template row: price (prominent)
                    with set depth underneath. typeof-guard so 0 still shows and
                    a missing value simply renders nothing. */}
                {(typeof row.price === "number" ||
                  typeof row.set === "number") && (
                  <div className="flex flex-col items-end leading-tight">
                    {typeof row.price === "number" && (
                      <span className="text-xs font-semibold text-gray-500">
                        {formatPrice(row.price)}
                      </span>
                    )}
                    {typeof row.set === "number" && (
                      <span className="text-xs text-gray-500">
                        {t("rituals.setDepth")}: {row.set}
                      </span>
                    )}
                  </div>
                )}
                {/* Set well apart from the price block, so it reads as a label
                    for the input next to it rather than part of the price. */}
                <span className="ml-16 text-xs text-gray-500">
                  {t("rituals.advisedQuantity", "advised quantity")}
                </span>
                <input
                  type="number"
                  min={1}
                  value={qty}
                  disabled={disabled}
                  onChange={(e) =>
                    setQtyOverrides((prev) => ({
                      ...prev,
                      [row.id]: Number(e.target.value),
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !addDisabled) {
                      e.preventDefault();
                      onRowAdd(row, qty);
                      setQtyOverrides((prev) => {
                        const { [row.id]: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="w-20 rounded-md border-0 py-1 px-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600 disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  disabled={addDisabled}
                  onClick={() => {
                    onRowAdd(row, qty);
                    setQtyOverrides((prev) => {
                      const { [row.id]: _, ...rest } = prev;
                      return rest;
                    });
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-gray-200 px-3 py-1 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <PlusCircleIcon className="h-4 w-4" aria-hidden="true" />
                  {t("rituals.add")}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* MainModal portals to <body>, so its place inside the scroll
          container doesn't matter. Only one theme is open at a time, so there
          is never more than one of these mounted. */}
      <MainModal
        type={WEEK_ORDER_IMAGE_PREVIEW_MODAL}
        closeHandler={closePreview}
        maxWidthClassName="sm:max-w-3xl"
      >
        {previewRow ? (
          <WeekOrderImagePreview
            title={formatWeekOrderArticleName(previewRow.article_name) || "—"}
            src={getImage(previewRow.article_name)?.preview}
            onClose={closePreview}
          />
        ) : (
          <></>
        )}
      </MainModal>
    </div>
  );
  //#endregion
}
